import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, ChevronRight, Upload, EyeOff, Trash2, Loader2, ImageIcon } from "lucide-react";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import { useUpdatePlaceStatus, type AdminPlaceRow } from "@/hooks/useAdminPlacesList";

interface Props {
  place: AdminPlaceRow;
  onDelete: (place: AdminPlaceRow) => void;
}

export function PlaceRowCard({ place, onDelete }: Props) {
  const navigate = useNavigate();
  const coverUrl = useCoverImageUrl("place", place.id, 320);
  const statusMutation = useUpdatePlaceStatus();
  const [busy, setBusy] = useState(false);

  const isPublished = place.status === "published";
  const editUrl = `/app/admin/places/${place.id}/edit`;

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await statusMutation.mutateAsync({
        id: place.id,
        newStatus: isPublished ? "draft" : "published",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={() => navigate(editUrl)}
      className="flex items-center gap-3 px-4 py-3 cursor-pointer active:bg-muted/60 transition-colors border-b last:border-b-0"
    >
      {/* Thumbnail */}
      {coverUrl ? (
        <img
          src={coverUrl}
          alt=""
          loading="lazy"
          className="h-11 w-11 rounded-lg object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{place.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {[place.municipality, place.landskap].filter(Boolean).join(", ") || "–"}
        </p>
      </div>

      {/* Status + actions */}
      <div className="flex items-center gap-1.5 shrink-0">
        <Badge
          variant={isPublished ? "default" : "secondary"}
          className="text-[10px] py-0 px-1.5"
        >
          {isPublished ? "Publicerad" : "Utkast"}
        </Badge>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem onClick={handleToggleStatus} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : isPublished ? (
                <EyeOff className="h-4 w-4 mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {isPublished ? "Avpublicera" : "Publicera"}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(place);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Radera
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  );
}
